import { db } from './db'

// ============================================================
// Token 缓存管理
// ============================================================

/** 模块级 token 缓存，由组件通过 setAuthToken 设置 */
let _cachedToken: string | null = null

/**
 * 设置认证 Token（由组件调用）
 * 在 AuthGuard 或其他认证组件中调用此函数来缓存 token
 */
export function setAuthToken(token: string | null): void {
  _cachedToken = token
}

/**
 * 获取认证 Token
 * 优先使用缓存的 token，其次尝试从 Instant DB 内部获取
 */
export async function getAuthToken(): Promise<string | null> {
  // 优先使用缓存的 token（由组件设置）
  if (_cachedToken) {
    return _cachedToken
  }

  // 备用：尝试从 Instant DB reactor 获取
  try {
    const reactor = (db as any)._reactor
    
    if (reactor?.getCurrentUser) {
      const result = await reactor.getCurrentUser()
      if (result?.user?.refresh_token) {
        return result.user.refresh_token
      }
    }

    if (reactor?.getCurrentUserCached) {
      const cached = reactor.getCurrentUserCached()
      if (cached?.user?.refresh_token) {
        return cached.user.refresh_token
      }
    }

    if (reactor?._currentUserCached?.user?.refresh_token) {
      return reactor._currentUserCached.user.refresh_token
    }
  } catch (error) {
    console.error('[API] 获取 Token 失败:', error)
  }

  console.warn('[API] ⚠️ 未能获取到 Auth Token')
  return null
}

/**
 * 带认证的 Fetch 封装
 */
export async function fetchWithAuth(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAuthToken()
  
  const headers = new Headers(options.headers || {})
  
  if (token) {
    // 确保没有 Bearer 前缀冲突
    const cleanToken = token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token.trim()
    headers.set('Authorization', `Bearer ${cleanToken}`)
    console.log(`[API] 请求 ${url}, 已附加 Authorization Header (长度: ${cleanToken.length})`)
  } else {
    console.warn(`[API] 请求 ${url}, 未能找到 Token，将不发送 Authorization Header`)
  }
  
  // 确保 Content-Type 默认为 application/json (如果是 POST/PUT 且 body 是字符串)
  if (!headers.has('Content-Type') && (options.method === 'POST' || options.method === 'PUT')) {
    if (typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json')
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // 如果返回 401，可能是 Token 过期或后端配置问题
  if (response.status === 401) {
    const errorData = await response.clone().json().catch(() => ({}))
    console.error('[API] 鉴权失败 (401):', {
      url,
      hasToken: !!token,
      error: errorData.error,
      code: errorData.code
    })
  }

  return response
}

